/**
 * Adapter d'impression EPSON M267D via API backend
 * Communique avec le service d'impression Node.js qui gère le protocole ESC/POS
 * Version 2.0 : Utilise la nouvelle API /epson-ticket avec config tenant persistée
 */

import {
  PrinterAdapter,
  PrintJob,
  PrinterStatus,
  PrinterError,
} from './PrinterAdapter';
import { api } from '../api';
import { logger } from '../../utils/logger';

export class EpsonM267DAdapter implements PrinterAdapter {
  readonly modelName = 'EPSON TM-M267D';
  readonly connectionType = 'CUPS' as const;

  private connected = false;
  private apiUrl = '/epson-ticket';

  /**
   * Configurer et connecter l'imprimante
   * La config est maintenant chargée automatiquement depuis le tenant
   */
  async connect(): Promise<void> {
    try {
      logger.log('[EpsonM267DAdapter] Vérification configuration tenant...');

      // Vérifier si la config existe dans le tenant
      const configResponse = await api.get<{
        success: boolean;
        config?: { connectionType: string; cupsQueueName?: string };
      }>(`${this.apiUrl}/config`);

      if (!configResponse.success || !configResponse.config?.cupsQueueName) {
        throw new PrinterError(
          'Configuration EPSON non trouvée. Veuillez configurer l\'imprimante dans Settings → Hardware.',
          'CONFIG_NOT_FOUND'
        );
      }

      this.connected = true;
      logger.log(
        '[EpsonM267DAdapter] Configuration chargée:',
        configResponse.config.cupsQueueName
      );
    } catch (error: any) {
      logger.error('[EpsonM267DAdapter] Erreur connexion:', error);
      throw new PrinterError(
        `Échec connexion EPSON M267D: ${error.message}`,
        'CONNECTION_ERROR',
        error
      );
    }
  }

  /**
   * Déconnecter l'imprimante
   */
  async disconnect(): Promise<void> {
    // Avec la nouvelle API, pas besoin de déconnecter explicitement
    // La config reste persistée dans le tenant
    this.connected = false;
    logger.log('[EpsonM267DAdapter] Imprimante déconnectée (config persistée)');
  }

  /**
   * Vérifier si connecté
   */
  isConnected(): boolean {
    return this.connected;
  }

  /**
   * Obtenir le statut de l'imprimante
   */
  async getStatus(): Promise<PrinterStatus> {
    try {
      const configResponse = await api.get<{
        success: boolean;
        config?: { cupsQueueName?: string };
      }>(`${this.apiUrl}/config`);

      const hasConfig = configResponse.success && !!configResponse.config?.cupsQueueName;

      return {
        connected: hasConfig,
        online: hasConfig,
        paperStatus: hasConfig ? 'ok' : 'unknown',
      };
    } catch (error) {
      return {
        connected: false,
        online: false,
        paperStatus: 'unknown',
        error: 'Impossible de vérifier le statut',
      };
    }
  }

  /**
   * Imprimer un ticket
   */
  async print(job: PrintJob): Promise<void> {
    if (!this.isConnected()) {
      // Essayer de se reconnecter automatiquement
      await this.connect();
    }

    try {
      logger.log(
        '[EpsonM267DAdapter] Envoi impression:',
        job.metadata?.saleNumber
      );

      // Envoyer le job au backend via la nouvelle API
      const response = await api.post<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/print`, {
        lines: job.lines,
        metadata: job.metadata,
      });

      if (!response.success) {
        throw new Error(response.message);
      }

      logger.log('[EpsonM267DAdapter] Impression réussie:', response.message);
    } catch (error: any) {
      logger.error('[EpsonM267DAdapter] Erreur impression:', error);
      throw new PrinterError(
        `Échec impression: ${error.message}`,
        'PRINT_ERROR',
        error
      );
    }
  }

  /**
   * Ouvrir le tiroir-caisse
   * Note: Pour l'instant non implémenté dans la nouvelle API
   */
  async openCashDrawer(): Promise<void> {
    logger.warn('[EpsonM267DAdapter] openCashDrawer non implémenté dans la nouvelle API');
    // TODO: Implémenter dans EpsonTicketPrintingService si nécessaire
  }

  /**
   * Test d'impression simple
   */
  async testPrint(): Promise<void> {
    try {
      logger.log('[EpsonM267DAdapter] Test d\'impression...');
      const response = await api.post<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/test`);

      if (!response.success) {
        throw new Error(response.message);
      }

      logger.log('[EpsonM267DAdapter] Test réussi:', response.message);
    } catch (error: any) {
      logger.error('[EpsonM267DAdapter] Erreur test:', error);
      throw new PrinterError(
        `Échec test impression: ${error.message}`,
        'PRINT_ERROR',
        error
      );
    }
  }
}
